import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { convertTimestamp } from 'src/utils/timeHelpers';

import Tooltip from 'src/components/ui/Tooltip';
import { selectStepsDisabledStatus } from 'src/slices/qamodules.slice';

const EventLogWrapper = styled.div`
    padding: 4px 8px 2px;
    font-family: 'SF Mono', monospace;
    font-size: 14px;

    --eventlog-time-color: var(--text-secondary);
    --eventlog-value-color: var(--text-primary);
    --eventlog-value-bg-color: ${({ $eventNameBgColor }) => ($eventNameBgColor ? `var(${$eventNameBgColor})` : 'var(--surface-raised)')};

    @container (width < 315px) {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: 8px;
    }

    ${({ $isInteractionError }) => $isInteractionError && `
        --eventlog-value-bg-color: var(--rose-1);
        --eventlog-value-color: #fff;
        --eventlog-time-color: var(--rose-1);
    `}
`;

const EventLogTime = styled.span`
    display: inline-block;
    vertical-align: middle;
    margin: 0 8px 0 0;
    color: var(--eventlog-time-color);

    &::before {
        content: '[';
    }

    &::after {
        content: ']';
    }

    @container (width < 315px) {
        display: none;
    }
`;

const EventLogValue = styled.span`
    display: inline-block;
    vertical-align: middle;
    padding: 2px 6px;
    border-radius: 4px;
    color: var(--eventlog-value-color);
    background: var(--eventlog-value-bg-color);
`;

const TooltipIcon = styled.span`
    display: inline-block;
    vertical-align: middle;
    margin: 0 0 0 8px;
`;

const MeasureText = styled.span`
    display: inline-block;
    vertical-align: middle;
    margin: 0 0 0 8px;
    font-size: 10px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: calc(100% - 200px);
`;

const MeasureSegment = styled.span`
    &:nth-child(1) { color: #2b7bb9; }
    &:nth-child(2) { color: #2d9d5c; }
    &:nth-child(3) { color: #d4782a; }

    &:not(:first-child)::before {
        content: '/';
        color: var(--text-disabled);
        margin: 0 2px;
    }
`;

const TOTAL_AD_AMOUNT = {
    1: 'Small',
    2: 'Medium',
    3: 'Large',
};

const EVENT_NAME_MAP = {
    pokiTrackingSdkStatusInitialized: { name: 'SDK initialized', bgColor: '--blue-1' },
    pokiTrackingScreenGameLoadingFinished: { name: 'Game loading finished', bgColor: '--blue-1' },
    pokiTrackingScreenGameplayStart: { name: 'Gameplay start', bgColor: '--green-1' },
    pokiTrackingScreenGameplayStop: { name: 'Gameplay stop', bgColor: '--green-1' },
    pokiTrackingScreenRewardedBreak: { name: 'Rewarded break', bgColor: '--orange-1', options: { size: null } },
    pokiTrackingScreenCommercialBreak: { name: 'Commercial break', bgColor: '--orange-1' },
    pokiTrackingScreenDisplayAdRequest: { name: 'Display ad request', bgColor: '--orange-1' },
    pokiTrackingScreenDisplayAdDestroy: { name: 'Display ad destroy', bgColor: '--orange-1' },
    pokiTrackingMeasure: { name: 'Measure', bgColor: '--purple-1' },
    pokiMessageSendScore: { name: 'Submit Score', bgColor: '--purple-1' },
    pokiMessageShowLeaderboard: { name: 'Show Leaderboard', bgColor: '--purple-1' },
};

export default function EventLog({ timestamp, eventName, eventData }) {
    const { totalAdAmount } = eventData || {};

    const stepDisabledStatus = useSelector(selectStepsDisabledStatus);

    const time = convertTimestamp(timestamp); // convert timestamp to [hh:mm:ss format]
    const event = EVENT_NAME_MAP[eventName]; // get event name and color from map

    // custom event might contain additional data and we need to display it
    const isMeasureEvent = eventName === 'pokiTrackingMeasure' && eventData;
    const isSendScoreEvent = eventName === 'pokiMessageSendScore';
    const isShowLeaderboardEvent = eventName === 'pokiMessageShowLeaderboard';
    const isRewardedBreak = eventName === 'pokiTrackingScreenRewardedBreak';

    // this is a special case when gameplay start event is fired without interaction
    const isGameplayStart = eventName === 'pokiTrackingScreenGameplayStart';
    const isGameplayStartDisabled = stepDisabledStatus?.['sdk-basics-gameplay-start'];

    const hasGameplayInteraction = isGameplayStart ? (eventData?.interaction ?? false) : false;

    const extendedEvent = {
        ...event,
        options: {
            ...event.options,
            ...(isRewardedBreak && totalAdAmount && { size: TOTAL_AD_AMOUNT[totalAdAmount] }),
        },
    };

    const renderEventAsTooltip = ({ content, error = false }) => (
        <Tooltip arrow content={content} placement="top" maxWidth="100%">
            <EventLogTime>{time}</EventLogTime>
            <EventLogValue $error={error}>
                {extendedEvent?.name || eventName}
                <TooltipIcon>
                    ⓘ
                </TooltipIcon>
            </EventLogValue>
        </Tooltip>
    );

    const renderMeasureEvent = () => {
        const { action, category, label } = eventData || {};
        const parts = [category, action, label].filter(Boolean);
        const fullText = parts.join(' / ');

        return (
            <Tooltip arrow content={fullText} placement="top" maxWidth="100%">
                <EventLogTime>{time}</EventLogTime>
                <EventLogValue>
                    {extendedEvent?.name || eventName}
                </EventLogValue>
                <MeasureText>
                    {parts.map((value, i) => (
                        <MeasureSegment key={i}>{value}</MeasureSegment>
                    ))}
                </MeasureText>
            </Tooltip>
        );
    };

    const renderSendScoreEvent = () => {
        const leaderboard = eventData?.leaderboard;
        const score = eventData?.score;

        return (
            <Tooltip arrow content={`Leaderboard: ${leaderboard}, Score: ${score}`} placement="top" maxWidth="100%">
                <EventLogTime>{time}</EventLogTime>
                <EventLogValue>
                    {extendedEvent?.name || eventName}
                </EventLogValue>
                <MeasureText>
                    {`${leaderboard} / ${score}`}
                </MeasureText>
            </Tooltip>
        );
    };

    const renderShowLeaderboardEvent = () => {
        const id = eventData?.id;

        return (
            <Tooltip arrow content={`ID: ${id}`} placement="top" maxWidth="100%">
                <EventLogTime>{time}</EventLogTime>
                <EventLogValue>
                    {extendedEvent?.name || eventName}
                </EventLogValue>
                <MeasureText>
                    {id}
                </MeasureText>
            </Tooltip>
        );
    }

    const renderEventLogValue = () => {
        if (isGameplayStart && !hasGameplayInteraction && isGameplayStartDisabled) {
            return renderEventAsTooltip({ content: 'Gameplay was fired without a player interaction', error: true });
        }

        if (Object.keys(extendedEvent?.options).length > 0) {
            return renderEventAsTooltip({ content: extendedEvent.options.size ? `Ad Size: ${extendedEvent.options.size}` : 'No additional data' } );
        }

        if (isMeasureEvent) {
            return renderMeasureEvent();
        }

        if (isSendScoreEvent) {
            return renderSendScoreEvent();
        }

        if (isShowLeaderboardEvent) {
            return renderShowLeaderboardEvent();
        }

        return (
            <>
                <EventLogTime>{time}</EventLogTime>
                <EventLogValue>
                    {extendedEvent?.name || eventName}
                </EventLogValue>
            </>
        );
    };

    return (
        extendedEvent && (
            <EventLogWrapper
                $eventNameBgColor={extendedEvent?.bgColor}
                $isInteractionError={isGameplayStart && !hasGameplayInteraction && isGameplayStartDisabled}
            >
                {renderEventLogValue()}
            </EventLogWrapper>
        )
    );
}

EventLog.propTypes = {
    timestamp: PropTypes.number.isRequired,
    eventName: PropTypes.string.isRequired,
    // we don't know what data will be passed to custom event
    eventData: PropTypes.object,
};

EventLog.defaultProps = {
    eventData: null,
};
